import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Menu, Clock, Trophy, Eye } from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion, AnimatePresence } from 'framer-motion'
import { pageContainer, fadeInUp, cardReveal, cardDeal, tapScale } from '@/lib/motion'
import { Drawer } from 'vaul'
import { useAuthStore } from '@/stores/authStore'
import {
  DealCardAnimation,
  ChipMoveAnimation,
  PotDistributeAnimation,
  FoldAnimation,
  CardFlipAnimation,
  WinCelebration,
  AnimationDemoPanel,
} from '@/components/game/GameAnimations'
import { ConnectionOverlay, type ConnectionStatus } from '@/components/shared/ConnectionOverlay'
import { PlayerNameplate, type PlayerData } from '@/components/game/PlayerNameplate'
import { ActionZone } from '@/components/game/ActionZone'
import { ChatBubble, type ChatBubbleData } from '@/components/game/ChatBubble'
import { MenuDrawer } from '@/components/game/MenuDrawer'

// ── Mock Data ──────────────────────────────────────────────────

const mockPlayers: PlayerData[] = [
  { seat: 0, name: '張三', avatar: '😎', stack: 15000, bet: 200, status: 'active', country: '🇹🇼', isDealer: false },
  { seat: 1, name: 'Empty', avatar: '', stack: 0, bet: 0, status: 'empty', country: '', isDealer: false },
  { seat: 2, name: '李四', avatar: '👩‍🦰', stack: 8500, bet: 0, status: 'folded', country: '🇭🇰', isDealer: false },
  { seat: 3, name: '王五', avatar: '🧢', stack: 22000, bet: 400, status: 'active', country: '🇨🇳', isDealer: true },
  { seat: 4, name: 'Empty', avatar: '', stack: 0, bet: 0, status: 'empty', country: '', isDealer: false },
  { seat: 5, name: '趙六', avatar: '👓', stack: 12000, bet: 200, status: 'current', country: '🇲🇾', isDealer: false },
  { seat: 6, name: 'Empty', avatar: '', stack: 0, bet: 0, status: 'empty', country: '', isDealer: false },
  { seat: 7, name: '孫七', avatar: '👘', stack: 5000, bet: 0, status: 'all_in', country: '🇸🇬', isDealer: false },
  { seat: 8, name: '周八', avatar: '🦁', stack: 18000, bet: 200, status: 'active', country: '🇹🇼', isDealer: false },
]

const communityCards = [
  { rank: 'A', suit: 'spades' },
  { rank: 'K', suit: 'hearts' },
  { rank: '10', suit: 'clubs' },
  { rank: '5', suit: 'diamonds' },
  { rank: '2', suit: 'spades' },
]

const heroHoleCards = [
  { rank: 'A', suit: 'hearts' },
  { rank: 'K', suit: 'spades' },
]

const mockChat = [
  { user: '王五', msg: '好牌！', time: '2m' },
  { user: '張三', msg: 'gg', time: '1m' },
  { user: '周八', msg: '這手打得漂亮', time: '30s' },
]

const disconnectedSeats = new Set([2])

// Showdown hand rankings (displayed near player at showdown)
const showdownRankings: Record<number, string> = {
  0: 'Top Pair',
  3: 'Pair of Queens',
  7: 'Pair of Nines',
}

// Room info
const roomId = '128503'
const gameType = 'Cash NLH'

// Showdown: revealed hole cards — only some players show (others stay face-down)
const showdownCards: Record<number, { rank: string; suit: string }[]> = {
  0: [{ rank: 'A', suit: 'hearts' }, { rank: 'K', suit: 'spades' }],   // hero
  3: [{ rank: 'Q', suit: 'hearts' }, { rank: 'Q', suit: 'diamonds' }], // 王五 — shows
  7: [{ rank: '9', suit: 'spades' }, { rank: '9', suit: 'hearts' }],   // 孫七 — shows
  // 趙六 (5), 周八 (8) — stay face-down
}

// ── Card helpers ───────────────────────────────────────────────

const getSuitColor = (suit: string) => suit === 'hearts' || suit === 'diamonds' ? 'text-red-500' : 'text-slate-800'
const getSuitIcon = (suit: string) => {
  switch (suit) {
    case 'spades': return '♠'
    case 'hearts': return '♥'
    case 'clubs': return '♣'
    case 'diamonds': return '♦'
    default: return ''
  }
}

// ── Seat positions as percentages of the felt element ─────────
// Portrait oval: hero at bottom center, clockwise around the table.
// Left/right are perfectly mirrored: 1↔8, 2↔7, 3↔6, 4↔5.

const SEAT_POSITIONS: { left: string; top: string }[] = [
  { left: '50%',  top: '93%'  },  // 0: Hero — bottom center
  { left: '8%',   top: '76%'  },  // 1: Lower-left
  { left: '0%',   top: '46%'  },  // 2: Mid-left
  { left: '10%',  top: '17%'  },  // 3: Upper-left
  { left: '34%',  top: '2%'   },  // 4: Top center-left
  { left: '66%',  top: '2%'   },  // 5: Top center-right
  { left: '90%',  top: '17%'  },  // 6: Upper-right
  { left: '100%', top: '46%'  },  // 7: Mid-right
  { left: '92%',  top: '76%'  },  // 8: Lower-right
]

// ── Felt aspect ratio & containment (portrait) ───────────────
const FELT_ASPECT = 0.74   // width/height — portrait (taller than wide)
const FELT_MAX_W = 0.92    // max 92% of table zone width
const FELT_MAX_H = 0.94    // max 94% of table zone height

// ── Bet position — interpolate 35% from seat toward felt center ──

function getBetStyle(seatIdx: number) {
  const seat = SEAT_POSITIONS[seatIdx]
  const sLeft = parseFloat(seat.left)
  const sTop = parseFloat(seat.top)
  const t = 0.35
  return {
    left: `${sLeft + (50 - sLeft) * t}%`,
    top: `${sTop + (50 - sTop) * t}%`,
  }
}

// ── Main Component ────────────────────────────────────────────

export function GameTableScreen() {
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  // Core game state
  const [isSpectator, setIsSpectator] = useState(true)
  const [showSitDown, setShowSitDown] = useState(false)
  const [selectedSeat, setSelectedSeat] = useState<number | null>(null)
  const [isSittingOut, setIsSittingOut] = useState(false)
  const [showRaise, setShowRaise] = useState(false)
  const [raiseAmount, setRaiseAmount] = useState(500)
  const [autoFold, setAutoFold] = useState(false)
  const [autoCheck, setAutoCheck] = useState(false)
  const [timerSeconds, setTimerSeconds] = useState(22)
  const inactivityFoldCount = useRef(0)
  const tableZoneRef = useRef<HTMLDivElement>(null)
  const [feltDims, setFeltDims] = useState<{ w: number; h: number } | null>(null)

  // Drawer state
  const [buyInOpen, setBuyInOpen] = useState(false)
  const [buyInAmount, setBuyInAmount] = useState(50000)
  const [rebuyOpen, setRebuyOpen] = useState(false)
  const [rebuyAmount, setRebuyAmount] = useState(20000)
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false)
  const [jackpotInfoOpen, setJackpotInfoOpen] = useState(false)
  const [largeRaiseConfirmOpen, setLargeRaiseConfirmOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  // Chat bubbles (spatial)
  const [chatBubbles, setChatBubbles] = useState<ChatBubbleData[]>([])
  const [chatMessages, setChatMessages] = useState(mockChat)

  // Animations / demo
  const [showDemoPanel, setShowDemoPanel] = useState(false)
  const [activeAnimation, setActiveAnimation] = useState<string | null>(null)
  const [showdown, setShowdown] = useState(true)

  // Connection
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null)
  const [inactivityMessage, setInactivityMessage] = useState(false)
  const [jackpotOptIn, setJackpotOptIn] = useState(true)

  // Constants
  const pot = 1800
  const spectatorCount = 15
  const stakes = '5/10'
  const minBuyIn = 20000
  const maxBuyIn = 100000
  const currentBet = 200
  const playerStack = 12000
  const jackpotAmount = 88888

  // ── Effects ────────────────────────────────────────────────

  // Felt sizing — fit within table zone while maintaining portrait aspect ratio
  useEffect(() => {
    const el = tableZoneRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const { width: zw, height: zh } = entry.contentRect
      // Portrait: constrain by height first, then check width
      let h = zh * FELT_MAX_H
      let w = h * FELT_ASPECT
      if (w > zw * FELT_MAX_W) {
        w = zw * FELT_MAX_W
        h = w / FELT_ASPECT
      }
      setFeltDims({ w, h })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Timer countdown
  useEffect(() => {
    if (!isSpectator && timerSeconds > 0) {
      const t = setTimeout(() => setTimerSeconds(s => s - 1), 1000)
      return () => clearTimeout(t)
    }
    if (!isSpectator && timerSeconds === 0) {
      setInactivityMessage(true)
      inactivityFoldCount.current += 1
      const dismiss = setTimeout(() => setInactivityMessage(false), 3000)
      if (inactivityFoldCount.current >= 3) {
        setIsSittingOut(true)
      }
      return () => clearTimeout(dismiss)
    }
  }, [timerSeconds, isSpectator])

  // ── Handlers ───────────────────────────────────────────────

  const handleSitDown = () => setShowSitDown(true)

  const handleSelectSeat = (seatIndex: number) => {
    if (mockPlayers[seatIndex].status === 'empty') {
      setSelectedSeat(seatIndex)
      setShowSitDown(false)
      setBuyInOpen(true)
    }
  }

  const handleBuyInConfirm = () => {
    setIsSpectator(false)
    setBuyInOpen(false)
    setSelectedSeat(null)
    setTimerSeconds(22)
  }

  const handleLeave = useCallback(() => {
    setLeaveConfirmOpen(false)
    navigate('/rooms')
  }, [navigate])

  const handleDemoAnimation = (type: string) => {
    setActiveAnimation(type)
    const duration = type === 'jackpot' ? 6000 : type.startsWith('win') ? 4000 : 2000
    setTimeout(() => setActiveAnimation(null), duration)
  }

  const handleSendEmoji = useCallback((emoji: string) => {
    const bubble: ChatBubbleData = {
      id: `${Date.now()}-${Math.random()}`,
      seatIndex: 0, // hero seat
      message: emoji,
      isEmoji: true,
      timestamp: Date.now(),
    }
    setChatBubbles(prev => [...prev, bubble])
    setChatMessages(prev => [...prev, { user: '你', msg: emoji, time: '剛剛' }])
  }, [])

  const handleExpireBubble = useCallback((id: string) => {
    setChatBubbles(prev => prev.filter(b => b.id !== id))
  }, [])

  // ── Derived state ──────────────────────────────────────────

  const actionState = (() => {
    if (isSpectator && !isAuthenticated) return 'spectator-unauth' as const
    if (isSpectator && !showSitDown) return 'spectator-auth' as const
    if (isSpectator && showSitDown) return 'seat-selection' as const
    if (isSittingOut) return 'sitting-out' as const
    if (showRaise) return 'raise' as const
    // Simulate: if timer is running, it's your turn
    if (timerSeconds > 0) return 'your-turn' as const
    return 'not-your-turn' as const
  })()

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-surface-sunken flex flex-col overflow-hidden">
      {/* Ambient lighting */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-gold/5 rounded-full blur-[120px]" />
      </div>
      <div className="fixed inset-0 noise-overlay pointer-events-none opacity-[0.02]" />

      {/* Portrait Game Layout */}
      <motion.div
        variants={pageContainer} initial="hidden" animate="visible"
        className="flex flex-col w-full h-full relative"
      >
        {/* ── Top Bar ── */}
        <motion.div variants={fadeInUp} className="relative z-50 flex items-center justify-between h-11 px-3 safe-area-top flex-shrink-0">
          {/* Left: Back */}
          <motion.button
            whileTap={tapScale}
            onClick={() => isSpectator ? navigate(isAuthenticated ? '/rooms' : '/login') : setLeaveConfirmOpen(true)}
            className="w-8 h-8 flex items-center justify-center rounded-full glass-card hover:border-gold/30 transition-all"
          >
            <ArrowLeft className="w-4 h-4 text-foreground" />
          </motion.button>

          {/* Center: Stakes + Game Info */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-foreground/60 tracking-wide">${stakes} NLH</span>
          </div>

          {/* Right: Spectators + Menu */}
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-black/20 backdrop-blur-sm">
              <Eye className="w-3 h-3 text-gold/60" />
              <span className="text-[10px] font-bold text-foreground/60">{spectatorCount}</span>
            </div>
            <motion.button
              whileTap={tapScale}
              onClick={() => setMenuOpen(true)}
              className="w-8 h-8 flex items-center justify-center rounded-full glass-card hover:border-gold/30 transition-all"
            >
              <Menu className="w-4 h-4 text-foreground" />
            </motion.button>
          </div>
        </motion.div>

        {/* ── Table Zone (flexible height) ── */}
        <div ref={tableZoneRef} className="flex-1 relative min-h-0">
          {/* Table felt — portrait oval, sized by ResizeObserver */}
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={feltDims ? { width: feltDims.w, height: feltDims.h } : { width: '85%', aspectRatio: FELT_ASPECT }}
          >
            {/* Felt background — portrait oval via 50% border-radius */}
            <div className="absolute inset-0 rounded-[50%] bg-gradient-to-b from-[#2a1f14] to-[#120c06] p-[5px] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
              <div className="absolute inset-[1px] rounded-[50%] border border-white/[0.06]" />
              <div className="relative w-full h-full rounded-[50%] overflow-hidden table-felt">
                <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[60%] h-[25%] bg-gradient-to-b from-white/[0.04] to-transparent rounded-full blur-2xl" />
                <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg viewBox=%220 0 100 100%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.8%22 numOctaves=%223%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E')] opacity-[0.15] mix-blend-overlay" />
              </div>
            </div>

            {/* Jackpot pill — top of felt */}
            <button
              onClick={() => setJackpotInfoOpen(true)}
              className="absolute top-[8%] left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 rounded-full bg-black/30 backdrop-blur-sm border border-gold/20 hover:border-gold/40 transition-all px-2.5 py-0.5"
            >
              <Trophy className="w-3 h-3 text-gold" />
              <span className="text-[10px] font-bold text-gradient-gold-shimmer font-mono">
                ${jackpotAmount.toLocaleString()}
              </span>
            </button>

            {/* Center: Pot + Community Cards + Table Info */}
            <div className="absolute left-1/2 top-[46%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center z-20 pointer-events-none gap-2">
              {/* Pot display */}
              <motion.div
                variants={cardReveal}
                className="rounded-full flex items-center pointer-events-auto bg-black/40 border border-emerald-500/20 px-3 py-1 gap-1.5"
              >
                <span className="text-white/60 text-[10px] font-bold uppercase tracking-wider">POT</span>
                <span className="text-white font-mono font-bold text-sm">${pot.toLocaleString()}</span>
              </motion.div>

              {/* Community Cards — portrait-sized with placeholders */}
              <div className="flex pointer-events-auto perspective-1000 gap-1.5">
                {[0, 1, 2, 3, 4].map((i) => {
                  const card = communityCards[i]
                  if (!card) {
                    // Placeholder for undealt cards
                    return (
                      <div key={i} className="w-[42px] h-[58px] rounded-[4px] bg-white/[0.06] border border-white/[0.08]" />
                    )
                  }
                  return (
                    <motion.div
                      key={i} variants={cardDeal} custom={i}
                      className="relative select-none w-[42px] h-[58px]"
                      style={{ transformStyle: 'preserve-3d' }}
                    >
                      <div className="absolute inset-0 bg-black/30 rounded-md blur-md translate-y-1" />
                      <div className="relative w-full h-full bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-[4px] border border-gray-200/80 overflow-hidden shadow-lg">
                        <div className={cn('absolute top-0.5 left-0.5 flex flex-col items-center leading-none', getSuitColor(card.suit))}>
                          <span className="font-black font-serif text-base">{card.rank}</span>
                          <span className="text-[10px] -mt-0.5">{getSuitIcon(card.suit)}</span>
                        </div>
                        <div className={cn('absolute bottom-1 right-1', getSuitColor(card.suit))}>
                          <span className="text-base">{getSuitIcon(card.suit)}</span>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-transparent" />
                      </div>
                    </motion.div>
                  )
                })}
              </div>

              {/* Table Info */}
              <div className="text-center pointer-events-auto">
                <p className="text-white/20 text-[8px] font-medium tracking-wider uppercase">
                  {gameType} · ${stakes} · #{roomId}
                </p>
              </div>
            </div>

            {/* Player Seats — percentage-based inside felt */}
            {mockPlayers.map((player, index) => (
              <div
                key={index}
                className="absolute -translate-x-1/2 -translate-y-1/2 z-30"
                style={SEAT_POSITIONS[index]}
              >
                {/* Chat bubble near seat */}
                <AnimatePresence>
                  {chatBubbles.filter(b => b.seatIndex === index).map(bubble => (
                    <ChatBubble key={bubble.id} bubble={bubble} onExpire={handleExpireBubble} />
                  ))}
                </AnimatePresence>

                <PlayerNameplate
                  player={player}
                  isCompact={false}
                  isDisconnected={disconnectedSeats.has(player.seat)}
                  isSittingOut={player.seat === 0 && isSittingOut && !isSpectator}
                  isHero={player.seat === 0}
                  holeCards={showdown && player.status !== 'folded' && player.status !== 'empty' ? showdownCards[player.seat] : undefined}
                  timerSeconds={player.status === 'current' ? timerSeconds : 0}
                  timerMax={30}
                  showSitDown={showSitDown}
                  onSelectSeat={() => handleSelectSeat(index)}
                  handRanking={showdown ? showdownRankings[player.seat] : undefined}
                />
              </div>
            ))}

            {/* Floating bet amounts — percentage-based inside felt */}
            {mockPlayers.map((player, index) => {
              if (player.bet <= 0 && player.status !== 'all_in') return null
              return (
                <motion.div
                  key={`bet-${index}`}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="absolute -translate-x-1/2 -translate-y-1/2 z-[35] pointer-events-none"
                  style={getBetStyle(index)}
                >
                  <div className="flex items-center gap-0.5 whitespace-nowrap">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-b from-yellow-400 to-yellow-600 border border-yellow-300/50 shadow-sm flex-shrink-0" />
                    <span className="text-[10px] font-mono font-semibold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                      ${player.bet / 100}
                    </span>
                  </div>
                </motion.div>
              )
            })}
          </div>

          {/* Spectator mode indicator */}
          <AnimatePresence>
            {isSpectator && !showSitDown && (
              <motion.div
                initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                className="absolute left-1/2 -translate-x-1/2 bottom-[12%] z-20 bg-black/40 backdrop-blur-sm rounded-full flex items-center gap-2 px-4 py-1.5"
              >
                <Eye className="w-3.5 h-3.5 text-gold/70" />
                <span className="text-xs font-semibold text-foreground/70">觀戰模式</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Inactivity fold toast */}
          <AnimatePresence>
            {inactivityMessage && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="absolute left-1/2 -translate-x-1/2 top-16 z-50 bg-black/60 backdrop-blur-sm rounded-full flex items-center border border-rose-500/30 px-5 py-2 gap-2"
              >
                <Clock className="w-4 h-4 text-rose-400" />
                <span className="text-xs font-bold text-rose-400">因閒置已自動棄牌</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* DEV: Debug buttons & demo panel */}
          {import.meta.env.DEV && (
            <>
              <div className="absolute left-3 bottom-3 z-50 flex gap-1.5">
                <button
                  onClick={() => setShowDemoPanel(!showDemoPanel)}
                  className={cn(
                    'px-2 py-1 rounded-lg text-[9px] font-bold transition-all',
                    showDemoPanel ? 'bg-gold text-background' : 'glass-card text-gold/60 hover:text-gold'
                  )}
                >
                  {showDemoPanel ? '隱藏' : '動畫'}
                </button>
                <button
                  onClick={() => {
                    setConnectionStatus((prev) => {
                      if (prev === null) return 'reconnecting'
                      if (prev === 'reconnecting') return 'disconnected'
                      if (prev === 'disconnected') return 'session-expired'
                      return null
                    })
                  }}
                  className="px-2 py-1 rounded-lg text-[9px] font-bold glass-card text-rose-400/60 hover:text-rose-400 transition-all"
                >
                  斷線
                </button>
                <button
                  onClick={() => setIsSittingOut(!isSittingOut)}
                  className={cn(
                    'px-2 py-1 rounded-lg text-[9px] font-bold transition-all',
                    isSittingOut ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'glass-card text-amber-400/60 hover:text-amber-400'
                  )}
                >
                  暫離
                </button>
                <button
                  onClick={() => setShowdown(!showdown)}
                  className={cn(
                    'px-2 py-1 rounded-lg text-[9px] font-bold transition-all',
                    showdown ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'glass-card text-blue-400/60 hover:text-blue-400'
                  )}
                >
                  攤牌
                </button>
              </div>

              <AnimatePresence>
                {showDemoPanel && (
                  <AnimationDemoPanel onTrigger={handleDemoAnimation} />
                )}
              </AnimatePresence>
            </>
          )}

          {/* Active animations */}
          <AnimatePresence>
            {activeAnimation === 'deal' && (
              <>
                {[0, 3, 5, 7, 8].map((seat, i) => {
                  // Convert percentage positions to pixel offsets from center
                  const sLeft = parseFloat(SEAT_POSITIONS[seat].left)
                  const sTop = parseFloat(SEAT_POSITIONS[seat].top)
                  return (
                    <DealCardAnimation
                      key={`deal-${seat}`}
                      targetX={(sLeft - 50) * 3}
                      targetY={(sTop - 50) * 3}
                      delay={i * 0.12}
                    />
                  )
                })}
              </>
            )}

            {activeAnimation === 'chips' && (
              <ChipMoveAnimation fromX={-120} fromY={80} toX={0} toY={-20} amount={200} />
            )}

            {activeAnimation === 'pot' && (
              <PotDistributeAnimation winnerX={-120} winnerY={80} amount={1800} />
            )}

            {activeAnimation === 'fold' && (
              <FoldAnimation fromX={-150} fromY={60} />
            )}

            {activeAnimation === 'showdown' && (
              <div className="absolute top-[30%] left-1/2 -translate-x-1/2 z-50 flex gap-3">
                <CardFlipAnimation rank="A" suit="spades" delay={0} />
                <CardFlipAnimation rank="K" suit="spades" delay={0.3} />
              </div>
            )}

            {activeAnimation === 'win-small' && (
              <WinCelebration size="small" amount={200} playerName="張三" onComplete={() => setActiveAnimation(null)} />
            )}
            {activeAnimation === 'win-medium' && (
              <WinCelebration size="medium" amount={1800} playerName="張三" onComplete={() => setActiveAnimation(null)} />
            )}
            {activeAnimation === 'win-big' && (
              <WinCelebration size="big" amount={12500} playerName="張三" onComplete={() => setActiveAnimation(null)} />
            )}
            {activeAnimation === 'jackpot' && (
              <WinCelebration size="jackpot" amount={88888} playerName="張三" onComplete={() => setActiveAnimation(null)} />
            )}
          </AnimatePresence>
        </div>

        {/* ── Action Zone ── */}
        <ActionZone
          state={actionState}
          isCompact={false}
          heroCards={heroHoleCards}
          handStrength={!isSpectator ? 'Top Pair' : undefined}
          currentBet={currentBet}
          pot={pot}
          playerStack={playerStack}
          raiseAmount={raiseAmount}
          onRaiseAmountChange={setRaiseAmount}
          autoFold={autoFold}
          autoCheck={autoCheck}
          onToggleAutoFold={() => setAutoFold(f => !f)}
          onToggleAutoCheck={() => setAutoCheck(c => !c)}
          onLogin={() => navigate('/login')}
          onSitDown={handleSitDown}
          onCancelSitDown={() => setShowSitDown(false)}
          onFold={() => {}}
          onCheck={() => {}}
          onCall={() => {}}
          onRaise={() => setShowRaise(true)}
          onConfirmRaise={() => {
            if (raiseAmount > playerStack * 0.5) {
              setLargeRaiseConfirmOpen(true)
            } else {
              setShowRaise(false)
            }
          }}
          onCancelRaise={() => setShowRaise(false)}
          onImBack={() => {
            setIsSittingOut(false)
            inactivityFoldCount.current = 0
            setTimerSeconds(22)
          }}
          onSendEmoji={handleSendEmoji}
          onOpenChat={() => setMenuOpen(true)}
        />

        {/* Connection Overlay */}
        <ConnectionOverlay
          status={connectionStatus ?? 'reconnecting'}
          visible={connectionStatus !== null}
          onRetry={() => setConnectionStatus(null)}
          onReturnToLobby={() => navigate(isAuthenticated ? '/rooms' : '/login')}
          onReLogin={() => navigate('/login')}
        />
      </motion.div>

      {/* ── Menu Drawer ── */}
      <MenuDrawer
        open={menuOpen}
        onOpenChange={setMenuOpen}
        isCompact={false}
        stakes={stakes}
        spectatorCount={spectatorCount}
        isSpectator={isSpectator}
        isSittingOut={isSittingOut}
        jackpotOptIn={jackpotOptIn}
        jackpotAmount={jackpotAmount}
        playerStack={playerStack}
        chatMessages={chatMessages}
        onToggleSitOut={() => setIsSittingOut(s => !s)}
        onRebuy={() => { setMenuOpen(false); setRebuyOpen(true) }}
        onToggleJackpot={() => setJackpotOptIn(j => !j)}
        onJackpotInfo={() => { setMenuOpen(false); setJackpotInfoOpen(true) }}
        onShare={() => {}}
        onLeave={() => { setMenuOpen(false); setLeaveConfirmOpen(true) }}
      />

      {/* ── Buy-In Drawer ── */}
      <Drawer.Root open={buyInOpen} onOpenChange={setBuyInOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-[#0a0c10] rounded-t-3xl border-t border-white/10 max-h-[80vh]">
              <div className="flex justify-center py-3">
                <div className="w-10 h-1.5 rounded-full bg-white/20" />
              </div>
              <div className="overflow-y-auto safe-area-bottom px-6 pb-8 max-h-[calc(80vh-36px)]">
                <Drawer.Title className="font-display text-2xl font-semibold text-foreground mb-1">買入</Drawer.Title>
                <Drawer.Description className="text-sm text-muted-foreground mb-6">
                  座位 {selectedSeat !== null ? selectedSeat + 1 : '-'} · 盲注 ${stakes}
                </Drawer.Description>
                <div className="text-center py-4">
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-2xl text-gold font-bold">$</span>
                    <span className="text-5xl font-mono font-bold text-foreground">{(buyInAmount / 100).toFixed(0)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {(buyInAmount / 1000).toFixed(0)} BB · 範圍 ${(minBuyIn / 100).toFixed(0)}-${(maxBuyIn / 100).toFixed(0)}
                  </p>
                </div>
                <input
                  type="range" min={minBuyIn} max={maxBuyIn} step={1000} value={buyInAmount}
                  onChange={(e) => setBuyInAmount(parseInt(e.target.value))}
                  className="w-full h-2 bg-surface-elevated rounded-full appearance-none accent-gold mb-4"
                />
                <div className="flex gap-2 mb-6">
                  {[
                    { label: '最低', val: minBuyIn },
                    { label: '50BB', val: 50000 },
                    { label: '最高', val: maxBuyIn },
                  ].map((p) => (
                    <button
                      key={p.label}
                      onClick={() => setBuyInAmount(p.val)}
                      className={cn(
                        'flex-1 py-3 rounded-xl text-sm font-bold transition-all',
                        buyInAmount === p.val
                          ? 'bg-gold/20 text-gold border border-gold/30'
                          : 'glass-card text-muted-foreground'
                      )}
                    >
                      {p.label}<br />
                      <span className="text-xs font-mono">${(p.val / 100).toFixed(0)}</span>
                    </button>
                  ))}
                </div>
                <div className="p-3 rounded-xl bg-surface-elevated border border-white/5 mb-6 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">可用餘額</span>
                  <span className="text-sm font-mono font-bold text-foreground">$1,050.00</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setBuyInOpen(false)} className="h-14 glass-card rounded-2xl font-semibold text-foreground">取消</button>
                  <button onClick={handleBuyInConfirm} className="h-14 btn-primary rounded-2xl font-bold text-lg">確認買入</button>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* ── Rebuy Drawer ── */}
      <Drawer.Root open={rebuyOpen} onOpenChange={setRebuyOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-[#0a0c10] rounded-t-3xl border-t border-white/10">
              <div className="flex justify-center py-3">
                <div className="w-10 h-1.5 rounded-full bg-white/20" />
              </div>
              <div className="overflow-y-auto safe-area-bottom px-6 pb-8">
                <Drawer.Title className="font-display text-2xl font-semibold text-foreground mb-1">補碼</Drawer.Title>
                <Drawer.Description className="text-sm text-muted-foreground mb-4">當前籌碼: <span className="font-mono font-bold text-gold">${(playerStack / 100).toFixed(0)}</span></Drawer.Description>
                <div className="text-center py-3">
                  <span className="text-4xl font-mono font-bold text-foreground">${(rebuyAmount / 100).toFixed(0)}</span>
                </div>
                <input
                  type="range" min={1000} max={maxBuyIn - playerStack} step={1000} value={rebuyAmount}
                  onChange={(e) => setRebuyAmount(parseInt(e.target.value))}
                  className="w-full h-2 bg-surface-elevated rounded-full appearance-none accent-gold mb-4"
                />
                <div className="flex gap-2 mb-6">
                  {[
                    { label: '最低', val: 1000 },
                    { label: '50BB', val: 50000 - playerStack },
                    { label: '補滿', val: maxBuyIn - playerStack },
                  ].map((p) => (
                    <button
                      key={p.label}
                      onClick={() => setRebuyAmount(Math.max(1000, p.val))}
                      className={cn(
                        'flex-1 py-2.5 rounded-xl text-xs font-bold transition-all',
                        rebuyAmount === p.val ? 'bg-gold/20 text-gold border border-gold/30' : 'glass-card text-muted-foreground'
                      )}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setRebuyOpen(false)} className="h-12 glass-card rounded-2xl font-semibold text-foreground">取消</button>
                  <button onClick={() => setRebuyOpen(false)} className="h-12 btn-primary rounded-2xl font-bold">確認補碼</button>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* ── Leave Confirmation Drawer ── */}
      <Drawer.Root open={leaveConfirmOpen} onOpenChange={setLeaveConfirmOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-[#0a0c10] rounded-t-3xl border-t border-white/10">
              <div className="flex justify-center py-3">
                <div className="w-10 h-1.5 rounded-full bg-white/20" />
              </div>
              <div className="safe-area-bottom px-6 pb-8 text-center">
                <Drawer.Title className="font-display text-2xl font-semibold text-foreground mb-2">離開牌桌？</Drawer.Title>
                <Drawer.Description className="text-sm text-muted-foreground mb-6">您的籌碼將會退回錢包</Drawer.Description>
                <div className="p-4 rounded-xl bg-surface-elevated border border-white/5 mb-6 flex items-center justify-between">
                  <span className="text-muted-foreground">當前籌碼</span>
                  <span className="font-mono font-bold text-gold text-lg">${(playerStack / 100).toFixed(0)}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setLeaveConfirmOpen(false)} className="h-14 glass-card rounded-2xl font-semibold text-foreground">繼續遊戲</button>
                  <button onClick={handleLeave} className="h-14 bg-rose-500 rounded-2xl font-bold text-white">確認離開</button>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* ── Jackpot Info Drawer ── */}
      <Drawer.Root open={jackpotInfoOpen} onOpenChange={setJackpotInfoOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-[#0a0c10] rounded-t-3xl border-t border-white/10 max-h-[80vh]">
              <div className="flex justify-center py-3">
                <div className="w-10 h-1.5 rounded-full bg-white/20" />
              </div>
              <div className="overflow-y-auto safe-area-bottom px-6 pb-8 max-h-[calc(80vh-36px)]">
                <Drawer.Title className="font-display text-2xl font-semibold text-foreground mb-1 flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-gold" />
                  累積獎金
                </Drawer.Title>
                <Drawer.Description className="text-sm text-muted-foreground mb-6">目前累積獎金池金額</Drawer.Description>
                <div className="p-6 rounded-3xl glass-card-elevated border-gold/30 text-center mb-6">
                  <p className="text-xs text-gold uppercase tracking-widest font-bold mb-2">當前獎金池</p>
                  <p className="text-5xl font-mono font-bold text-gradient-gold-shimmer mb-2">${jackpotAmount.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">每手抽取 $0.50 貢獻至獎金池</p>
                </div>
                <div className="mb-6">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-bold mb-3">獎金分配</p>
                  <div className="space-y-2">
                    {[
                      { hand: '皇家同花順', payout: '100%', color: 'text-gold' },
                      { hand: '同花順', payout: '20%', color: 'text-amber-400' },
                      { hand: '四條', payout: '5%', color: 'text-amber-300' },
                    ].map((tier) => (
                      <div key={tier.hand} className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                        <span className="text-sm font-medium text-foreground">{tier.hand}</span>
                        <span className={cn('text-sm font-mono font-bold', tier.color)}>{tier.payout}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mb-6">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-bold mb-3">近期中獎</p>
                  <div className="space-y-2">
                    {[
                      { name: '阿明', hand: '四條 A', amount: '$4,444', time: '2天前' },
                      { name: 'Sarah', hand: '同花順', amount: '$17,777', time: '5天前' },
                    ].map((winner, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                        <div>
                          <span className="text-sm font-medium text-foreground">{winner.name}</span>
                          <span className="text-xs text-muted-foreground ml-2">{winner.hand}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-mono font-bold text-gold">{winner.amount}</span>
                          <span className="text-[10px] text-muted-foreground ml-2">{winner.time}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-4 rounded-xl glass-card flex items-center justify-between mb-6">
                  <div>
                    <p className="text-sm font-semibold text-foreground">每手參加</p>
                    <p className="text-xs text-muted-foreground">每手貢獻 $0.50</p>
                  </div>
                  <button
                    onClick={() => setJackpotOptIn(!jackpotOptIn)}
                    className={cn(
                      'w-14 h-8 rounded-full transition-all duration-200 relative',
                      jackpotOptIn ? 'bg-gold' : 'bg-surface-elevated border border-white/10'
                    )}
                  >
                    <div className={cn(
                      'w-6 h-6 rounded-full bg-white shadow-md absolute top-1 transition-all duration-200',
                      jackpotOptIn ? 'left-7' : 'left-1'
                    )} />
                  </button>
                </div>
                <button onClick={() => setJackpotInfoOpen(false)} className="w-full h-14 btn-primary rounded-2xl font-bold text-lg">確認</button>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* ── Large Raise Confirmation Drawer ── */}
      <Drawer.Root open={largeRaiseConfirmOpen} onOpenChange={setLargeRaiseConfirmOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-[#0a0c10] rounded-t-3xl border-t border-white/10">
              <div className="flex justify-center py-3">
                <div className="w-10 h-1.5 rounded-full bg-white/20" />
              </div>
              <div className="safe-area-bottom px-6 pb-8 text-center">
                <Drawer.Title className="font-display text-2xl font-semibold text-foreground mb-2">確認大額加注</Drawer.Title>
                <Drawer.Description className="text-sm text-muted-foreground mb-6">此加注超過您籌碼的 50%</Drawer.Description>
                <div className="space-y-3 mb-6">
                  <div className="p-4 rounded-xl bg-surface-elevated border border-white/5 flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">加注金額</span>
                    <span className="font-mono font-bold text-gold text-lg">${(raiseAmount / 100).toFixed(0)}</span>
                  </div>
                  <div className="p-4 rounded-xl bg-surface-elevated border border-white/5 flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">加注後剩餘</span>
                    <span className="font-mono font-bold text-foreground text-lg">${((playerStack - raiseAmount) / 100).toFixed(0)}</span>
                  </div>
                  <div className="p-4 rounded-xl bg-surface-elevated border border-white/5 flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">佔籌碼比例</span>
                    <span className={cn(
                      'font-mono font-bold text-lg',
                      raiseAmount / playerStack > 0.8 ? 'text-rose-400' : 'text-amber-400'
                    )}>
                      {Math.round((raiseAmount / playerStack) * 100)}%
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setLargeRaiseConfirmOpen(false)} className="h-14 glass-card rounded-2xl font-semibold text-foreground">取消</button>
                  <button onClick={() => { setLargeRaiseConfirmOpen(false); setShowRaise(false) }} className="h-14 btn-primary rounded-2xl font-bold text-lg">確認加注</button>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  )
}
